import { z } from "zod";

// ---------------------------------------------------------------------------
// パスキー（#113、docs/43 の2回目。§1-3 製品のドメインごと・§3.3）
//   WebAuthn。DB は公開鍵・credential_id・counter・名前だけ（秘密鍵は端末側）。rp_id は製品のドメイン
//   （genbaryoku.jp／eigyouryoku.jp、開発は localhost）＝現場力で足した鍵は営業力では使えない。
//   画面の文はここに1か所（API のエラーの title も）
// ---------------------------------------------------------------------------

/** 設定 → ログイン方法の1行（この製品のドメインのものだけ） */
export const Passkey = z.object({
  id: z.string().uuid(),
  label: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  /** 同期される鍵（iCloud キーチェーン・Google パスワードマネージャー等） */
  synced: z.boolean(),
});
export type Passkey = z.infer<typeof Passkey>;

/**
 * 端末の候補を今の状態に合わせる合図（WebAuthn の Signal API、#114、docs/43 §14）に渡す値。
 *   userId は登録のときに user.id として渡したもの（人の番号＝accounts の uuid の16バイト、base64url。人ごと・製品が違っても同じ）
 */
export const PasskeySignal = z.object({
  rpId: z.string(),
  userId: z.string(),
  /** この人のこの製品（rp_id）の鍵の credential_id（base64url）。これに無い鍵は端末が候補から消してよい */
  acceptedCredentialIds: z.array(z.string()),
  /** 端末の候補に出る名前（name はメールアドレス、displayName は氏名・無ければメールアドレス） */
  name: z.string(),
  displayName: z.string(),
});
export type PasskeySignal = z.infer<typeof PasskeySignal>;

/** GET /account/passkeys（ログイン直後の案内と、端末の候補を合わせる合図が使う） */
export const PasskeyList = z.object({
  passkeys: z.array(Passkey),
  /** 案内「次回からパスキーでログイン」を出してよいか（この製品のパスキーが無い・まだ断っていない） */
  prompt: z.boolean(),
  signal: PasskeySignal,
});
export type PasskeyList = z.infer<typeof PasskeyList>;

/** PATCH /account/passkeys/:id */
export const PasskeyRenameRequest = z.object({
  label: z.string().trim().min(1, "名前を入れてください").max(50, "名前は50文字までです"),
});
export type PasskeyRenameRequest = z.infer<typeof PasskeyRenameRequest>;

/** POST /auth/passkey/register/verify・/login/verify の本文（response は @simplewebauthn/browser の戻り値そのまま。中身は API が確かめる） */
export const PasskeyRegisterVerifyRequest = z.object({ response: z.record(z.string(), z.unknown()) });
export type PasskeyRegisterVerifyRequest = z.infer<typeof PasskeyRegisterVerifyRequest>;
export const PasskeyLoginVerifyRequest = z.object({ response: z.record(z.string(), z.unknown()), remember: z.boolean().default(true) });
export type PasskeyLoginVerifyRequest = z.infer<typeof PasskeyLoginVerifyRequest>;

export const PASSKEY_MSG = {
  loginButton: "パスキーでログイン",
  title: "パスキー",
  add: "パスキー追加",
  empty: "まだありません",
  /** 「2026/10/01 に追加 ・ 最後に使った 10/01」（Figma「設定 ログイン方法」） */
  addedOn: (date: string) => `${date} に追加`,
  lastUsed: (md: string) => `最後に使った ${md}`,
  onlyMethod: "唯一のログイン方法",
  rename: "名前変更",
  renameLabel: "パスキーの名前",
  renameSave: "名前保存",
  added: "パスキーを追加しました",
  removed: "パスキーを外しました",
  renamed: "名前を変更しました",
  unsupported: "このブラウザはパスキーに対応していません",
  // ログイン直後の案内（Figma「案内：次回からパスキーでログイン」）
  promptTitle: "次回からパスキーでログイン",
  promptBody: "顔・指紋・端末のロックで、パスワードを打たずにログインできます。",
  promptLater: "今はしない",
  promptAdd: "パスキー追加",
  /** 設定の「？」（docs/10 §5.1.1） */
  help: (product: string) => `パスキーは、この端末の顔・指紋・画面ロックでログインする方法です。${product}のサイトごとに追加します。`,
} as const;

/** エラーの文（API の title。DB が RAISE するものは auth.ts の AUTH_ERROR がここを引く） */
export const PASSKEY_ERROR = {
  AUTH_PASSKEY_VERIFY_FAILED: "パスキーを確認できませんでした",
  AUTH_PASSKEY_COUNTER: "パスキーを確認できませんでした",
  AUTH_PASSKEY_CHALLENGE_INVALID: "パスキーを確認できませんでした",
  AUTH_PASSKEY_CHALLENGE_EXPIRED: "時間が経ちました。もう一度お試しください",
  AUTH_PASSKEY_NOT_FOUND: "このパスキーは登録されていません",
  /** どの製品にも無い鍵（#114）。画面は端末に「この鍵は無い」と合図して候補から消してもらう（signalUnknownCredential） */
  AUTH_PASSKEY_UNKNOWN: "このパスキーは登録されていません",
  AUTH_PASSKEY_EXISTS: "このパスキーはもう登録されています",
  AUTH_PASSKEY_LIMIT: "パスキーは20個までです。使っていないものを外してください",
  AUTH_PASSKEY_LABEL_INVALID: "名前は1〜50文字で入れてください",
} as const;
export type PasskeyErrorCode = keyof typeof PASSKEY_ERROR;

/**
 * パスキーの名前の自動（aaguid＝鍵の種類の番号から分かるもの）。分からなければ API が端末の名前（clientLabel）を使う。
 * 出どころ：passkey-authenticator-aaguids（コミュニティの一覧）のうち、利用者に多いものだけ
 */
export const PASSKEY_AAGUID_NAMES: Record<string, string> = {
  "fbfc3007-154e-4ecc-8c0b-6e020557d7bd": "iCloud キーチェーン",
  "dd4ec289-e01d-41c9-bb89-70fa845d4bf2": "iCloud キーチェーン",
  "ea9b8d66-4d01-1d21-3ce4-b6b48cb575d4": "Google パスワードマネージャー",
  "adce0002-35bc-c60a-648b-0b25f1f05503": "Chrome（Mac）",
  "08987058-cadc-4b81-b6e1-30de50dcbe96": "Windows Hello",
  "9ddd1817-af5a-4672-a2b9-3e3dd95000a9": "Windows Hello",
  "6028b017-b1d4-4c02-b4b3-afcdafc96bb2": "Windows Hello",
  "bada5566-a7aa-401f-bd96-45619a55120d": "1Password",
  "d548826e-79b4-db40-a3d8-11116f7e8349": "Bitwarden",
  "531126d6-e717-415c-9320-3d9aa6981239": "Dashlane",
  "53414d53-554e-4700-0000-000000000000": "Samsung Pass",
};

/** aaguid → 名前（無い・全部 0 なら null） */
export function passkeyNameOf(aaguid: string | null | undefined): string | null {
  if (!aaguid) return null;
  return PASSKEY_AAGUID_NAMES[aaguid.toLowerCase()] ?? null;
}
